import { Timestamp } from 'firebase/firestore';
import { Button, Badge, Card } from '../ui';
import { formatInTimezone } from '@/shared/utils/timezone';
import type { WorkoutSession, WorkoutTemplate } from '../../types';

interface QuickActionsProps {
  dailyTemplate: WorkoutTemplate | null;
  dailyInProgressSession: WorkoutSession | null;
  rotationTemplates: WorkoutTemplate[];
  rotationInProgressSessions: WorkoutSession[];
  inProgressSessions: WorkoutSession[];
  timezone?: string;
  onStartDaily: () => void;
  onStartRotation: (template: WorkoutTemplate) => void;
  onNavigateSession: (sessionId: string) => void;
}

export function QuickActions({
  dailyTemplate,
  dailyInProgressSession,
  rotationTemplates,
  rotationInProgressSessions,
  inProgressSessions,
  timezone,
  onStartDaily,
  onStartRotation,
  onNavigateSession,
}: QuickActionsProps) {
  return (
    <>
      {dailyTemplate && (
        <Card padding="lg" variant="elevated" data-testid="daily-workout-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
                Daily Workout
              </p>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {dailyTemplate.name}
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                {dailyTemplate.items.length} exercise{dailyTemplate.items.length !== 1 ? 's' : ''}
              </p>
            </div>
            {dailyInProgressSession ? (
              <Button
                size="sm"
                variant="secondary"
                data-testid="resume-daily-button"
                onClick={() => onNavigateSession(dailyInProgressSession.id)}
              >
                Resume
              </Button>
            ) : (
              <Button
                size="sm"
                variant="primary"
                data-testid="start-daily-button"
                onClick={onStartDaily}
              >
                Start
              </Button>
            )}
          </div>
        </Card>
      )}

      {rotationTemplates.length > 0 && (
        <Card padding="lg" variant="elevated" data-testid="rotation-templates-card">
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
                Quick Start
              </p>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Rotation Workouts
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                All templates currently in your rotation.
              </p>
            </div>
            <div className="space-y-2">
              {rotationTemplates.map((template, index) => {
                const inProgressSession = rotationInProgressSessions.find(
                  (session) => session.workoutTemplateId === template.id
                );

                return (
                  <div
                    key={template.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {index + 1}. {template.name}
                        </p>
                        {inProgressSession && (
                          <Badge variant="primary" size="sm">In progress</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-slate-400">
                        {template.items.length} exercise{template.items.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {inProgressSession ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onNavigateSession(inProgressSession.id)}
                      >
                        Resume
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => onStartRotation(template)}
                      >
                        Start
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* In-Progress Sessions (Resume) */}
      {inProgressSessions.length > 0 && (
        <Card padding="lg" variant="elevated" className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800">
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Resume Workout
                </h3>
                <p className="text-sm text-gray-600 dark:text-slate-400">
                  You have {inProgressSessions.length} session(s) in progress
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {inProgressSessions
                .sort((a, b) => (b.startedAt as Timestamp).toMillis() - (a.startedAt as Timestamp).toMillis())
                .map((session) => (
                  <button
                    key={session.id}
                    onClick={() => onNavigateSession(session.id)}
                    className="w-full text-left p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-white dark:bg-white/5 hover:bg-amber-50 dark:hover:bg-white/10 transition"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {session.workoutSnapshot.name}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-slate-400">
                          Started {formatInTimezone(
                            (session.startedAt as Timestamp).toDate(),
                            timezone
                          )}
                        </p>
                      </div>
                      <Badge variant="primary" size="sm">
                        Resume
                      </Badge>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </Card>
      )}
    </>
  );
}
